import { Button, Switch, Tooltip } from "antd";
import { ButtonProps } from "antd/lib";
import { useTypedFeatureIsOn } from "config/Growthbook/growthbookUtils";
import pluralize from "pluralize";
import { ReactNode } from "react";
import { MdOutlineOpenInNew, MdOutlineTune } from "react-icons/md";
import { Link } from "react-router-dom";
import { currentBusinessGetter } from "store/user/userSlice";

type CurrentBusinessType = ReturnType<typeof currentBusinessGetter>;

// === SmallSquareButton ===
const SmallSquareButton = (buttonProps: ButtonProps) => (
  <Button
    {...buttonProps}
    className="!h-6 aspect-square"
    size="small"
    type="link"
  />
);

// === GenerateButton ===
type GenerateButtonProps = ButtonProps & {
  title: string;
  inputArchived?: boolean;
};

export const GenerateButton = ({
  title,
  inputArchived,
  ...buttonProps
}: GenerateButtonProps) => {
  const showNewInputFeatures = useTypedFeatureIsOn("new-generator-panel");

  return showNewInputFeatures ? (
    <Tooltip
      title={
        inputArchived
          ? `${title} no longer in your library`
          : buttonProps.disabled
            ? ""
            : `Generate from ${title}`
      }
    >
      <SmallSquareButton
        {...buttonProps}
        disabled={buttonProps.disabled || inputArchived}
        icon={<MdOutlineTune size={14} />}
      />
    </Tooltip>
  ) : null;
};

// === ThemeToggleButton ===
type ThemeToggleButtonProps = {
  themeName: string | undefined;
  currentBusiness: CurrentBusinessType | null;
  handleToggleTheme: () => Promise<void>;
};

export const ThemeToggleButton = ({
  themeName,
  currentBusiness,
  handleToggleTheme,
}: ThemeToggleButtonProps) => {
  const isThemeOn = currentBusiness?.brand?.themes?.includes(themeName ?? "");
  const tooltipTitle = isThemeOn
    ? "Disable theme for future posts"
    : "Enable theme for future posts";

  return (
    <Tooltip title={tooltipTitle}>
      <Switch
        checked={isThemeOn}
        onChange={handleToggleTheme}
        disabled={!themeName}
        size="small"
        className="ml-2"
      />
    </Tooltip>
  );
};

// === InputDetail ===
type InputDetailProps = {
  children: ReactNode;
  pagePath: string;
  className?: string;
  hideColon?: boolean;
  pluralizeTooltips?: boolean;
  currentBusiness: CurrentBusinessType | null;
  handleToggleTheme: () => Promise<void>;
  themeName?: string;
  onGenerate?: () => void;
} & GenerateButtonProps;

export const InputDetail = ({
  title,
  children,
  pagePath,
  className,
  hideColon,
  pluralizeTooltips,
  currentBusiness,
  handleToggleTheme,
  themeName,
  onGenerate,
  ...generateButtonProps
}: InputDetailProps) => (
  <div className={`flex max-w-full items-center ${className}`}>
    <span className="font-semibold">
      {title}
      {!hideColon && ":"}
    </span>
    {typeof children === "string" ? (
      <span className="ml-1 order-0 overflow-hidden text-ellipsis whitespace-nowrap">
        {children}
      </span>
    ) : (
      children
    )}
    {onGenerate && (
      <GenerateButton
        title={title}
        onClick={onGenerate}
        {...generateButtonProps}
      />
    )}
    {title === "Theme" && currentBusiness && handleToggleTheme && (
      <ThemeToggleButton
        themeName={themeName}
        currentBusiness={currentBusiness}
        handleToggleTheme={handleToggleTheme}
      />
    )}
    <Tooltip title={`Go to ${pluralizeTooltips ? pluralize(title, 2) : title}`}>
      <Link to={pagePath}>
        <SmallSquareButton icon={<MdOutlineOpenInNew size={14} />} />
      </Link>
    </Tooltip>
  </div>
);
